import { inject, Injectable } from '@angular/core';
import { Observable, BehaviorSubject, Subject } from 'rxjs';
import { ApiService } from '../../services/apiservice/apiservice.service';
import { ConfigService } from '../../services/config/config.service';
import { HttpClient } from '@angular/common/http';
import { filter, map } from 'rxjs/operators';
import { RoutingService } from '../../services/routing/routing.service';
import { LOGIN_STATE, SharedService } from '../../services/shared/shared.service';
import { PredefinedTemplatesService } from '../../services/edit/predefined-templates.service';
import { Router } from '@angular/router';
import { Title } from '@angular/platform-browser';

export interface StaffMenuItem {
  items: MenuItem[];
  label: string;
  languageId: number;
  menuId: number;
  menuType: string;
  isEnabled: boolean;
  expanded: boolean; // UI State. Not part of the API.
}

export interface MenuItem {
  label?: string;
  menuId?: number;
  menuItem?: number;
  url: string;
  urlType?: string;
  fullLabel: string;
  subItems?: MenuItem[];
}

export class PersistentBehaviorSubject<T> extends BehaviorSubject<T> {
  private _key;

  constructor(_key: string, _value: T) {
    const data = sessionStorage.getItem(_key);
    if (data) {
      super(JSON.parse(data) as T);
    } else {
      super(_value);
    }

    this._key = _key;
  }

  next(value: T): void {
    super.next(value);
    sessionStorage.setItem(this._key, JSON.stringify(value));
  }
}

export enum MenuItemIds {
  RequestManager = 55,
  RequestManagerConfig = 513,
  ConfigureShippingLabel = 549,
}

@Injectable()
export class DashboardService {
  private readonly _helpManualsUrl = 'https://www5.auto-graphics.com/folder2/HTML_Manuals';

  private _staffDashboardMenu = new BehaviorSubject<StaffMenuItem[]>(null);
  private _staffHistory = new PersistentBehaviorSubject<MenuItem[]>('recentHistory', []);
  private _previousNonStaffRoute: string;
  private _previousNonStaffTitle: string;
  private _staffPageIsDisplayed: boolean;
  private _closeStaff$ = new Subject<void>();
  readonly closeStaff$ = this._closeStaff$.asObservable();

  staffDashboardMenu$ = this._staffDashboardMenu.asObservable();
  staffHistory$ = this._staffHistory.asObservable();

  private _http = inject(HttpClient);
  private _router = inject(Router);
  private _apiService = inject(ApiService);
  private _configService = inject(ConfigService);
  private _predefinedTemplatesService = inject(PredefinedTemplatesService);
  private _routingService = inject(RoutingService);
  private _sharedService = inject(SharedService);
  private _titleService = inject(Title);
  constructor() {
    this._sharedService
      .getLoginStatusBS()
      .pipe(filter((status) => status == LOGIN_STATE.LOGOUT))
      .subscribe(() => {
        this._staffHistory.next([]);
      });
  }

  loadStaffDashboard(): Observable<StaffMenuItem> {
    const url = this._configService.agApi + '/staff/dashboard';
    return this._apiService.get(url).pipe(
      map((response) => {
        if (response.status == '200 OK') {
          const result = response.data.staffMenu ?? [];
          this._staffDashboardMenu.next(result);
          return result;
        }

        //TODO Error Handling
        return [];
      }),
    );
  }

  async updateDashboardLinkInfoAsync(body) {
    try {
      const url = `${this._configService.agApi}/staff/link`;
      const response: any = await this._http.patch(url, body).toPromise();
      if (response) {
        if (
          response.status == '200 OK' &&
          response.message.toLowerCase() == 'success' &&
          response.data !== null
        ) {
          return response.data;
        }
      }
    } catch (error) {
      console.error(`dahboardservice:updateDashboardLinkInfoAsync => ${error.message}`);
    }
    return null;
  }

  openHelpLocationGroups(): void {
    window.open(`${this._helpManualsUrl}/Circ_Administration/Managing_Location_Groups.htm`, 'help');
  }

  closeStaff() {
    this._closeStaff$.next();
    this._titleService.setTitle(this._previousNonStaffTitle);
    this._sharedService.setOpenStaffPage(null);
    if (this._previousNonStaffRoute) {
      this._routingService.goToUrl(this._previousNonStaffRoute);
      this._previousNonStaffRoute = null;
      this._previousNonStaffTitle = null;
    } else {
      this._routingService.goToDashboard();
    }
  }

  goToStaffUrl(item: MenuItem, closeUrl: string) {
    if (closeUrl) {
      this._previousNonStaffRoute = closeUrl;
    }

    this._sharedService.setOpenStaffPage({ ...item });
  }

  goToMenuItem(itemId: MenuItem | number) {
    let item: MenuItem;

    if (typeof itemId == 'number') {
      item = this.findMenuItem(itemId);
    } else {
      item = itemId;
    }

    if (!item) {
      return;
    }

    if (!this._staffPageIsDisplayed) {
      this._previousNonStaffRoute = this._router.url;
      this._previousNonStaffTitle = this._titleService.getTitle();
    }

    this.pushStaffLinksHistory(item);

    const url = item.url;
    switch (url) {
      case 'ux':
        this._sharedService.setOpenSideNavSettings(true);
        break;
      case 'blankrequest':
        const queryParams = {
          agctrlid: -1,
          shard: -1,
          format: 'none',
          isCill: true,
          requestType: 'loan',
          requestFormType: 'blank',
          multiCopy: false,
          data: Date.now(),
        };
        this._routingService.goToIllRequest(queryParams);
        break;
      default:
        if (url.toLowerCase().includes('customer-care')) {
          this._routingService.goToPageById(
            this._predefinedTemplatesService.getCustomerCarePageBSValue(),
          );
          break;
        }

        if (item.urlType == 'route') {
          this._sharedService.setOpenStaffPage(null);
          this._routingService.goToUrl(item.url);
        } else {
          this._sharedService.setOpenStaffPage({ ...item });
        }
    }
  }

  fullLabel(menuItem: number): string {
    if (!menuItem) {
      return;
    }

    let fullLabel;
    this._staffDashboardMenu.value.forEach((m) => {
      if (fullLabel) {
        return null;
      }

      m.items.forEach((i) => {
        if (i.menuItem == menuItem) {
          fullLabel = `${m.label} > ${i.label}`;
        }

        if (!fullLabel) {
          i.subItems.forEach((s) => {
            if (s.menuItem == menuItem) {
              fullLabel = `${m.label} > ${i.label} > ${s.label}`;
            }
          });
        }
      });
    });

    return fullLabel;
  }

  findMenu(menuId: number): StaffMenuItem {
    if (!menuId) {
      return;
    }
    return this._staffDashboardMenu.value.find((m) => m.menuId == menuId);
  }

  findMenuItem(menuItem: number): MenuItem {
    let foundItem;

    this._staffDashboardMenu.value.forEach((m) => {
      if (foundItem) {
        return null;
      }

      m.items.forEach((i) => {
        if (i.menuItem == menuItem) {
          foundItem = i;
        }

        if (!foundItem) {
          i.subItems.forEach((s) => {
            if (s.menuItem == menuItem) {
              foundItem = s;
            }
          });
        }
      });
    });

    return foundItem;
  }

  toggleStaffNavigationDisplay(status: boolean) {
    this._staffPageIsDisplayed = status;
  }

  pushStaffLinksHistory(item: MenuItem) {
    const recentStaffPages = this._staffHistory.value;
    // only add if not found or not first index AKA last page
    const findIndex = recentStaffPages.findIndex((i) => i.menuItem == item.menuItem);
    if (findIndex == 0) {
      return;
    }

    if (recentStaffPages.unshift(item) > 3) {
      recentStaffPages.pop();
    }

    this._staffHistory.next(recentStaffPages);
  }

  popStaffLinksHistory() {
    let recentStaffPages = this._staffHistory.value;
    if (!recentStaffPages) {
      return;
    }

    const nextIndex = this._staffPageIsDisplayed ? 1 : 0;
    const item = recentStaffPages[nextIndex];
    if (!item) {
      return;
    }

    recentStaffPages = recentStaffPages.slice(nextIndex + 1);
    this._staffHistory.next(recentStaffPages);

    this.goToMenuItem(item);
  }
}
